import React, { useEffect, useRef, useState } from "react";
import { Search, X } from "lucide-react";
import { MAX_QUERY_LENGTH } from "../../../lib/kitsBrowse";

// design doc section 6. `q` is the committed value (from the URL); this
// component keeps its own `draft` for instant echo while typing and commits
// 250ms after the last keystroke, or on Enter/blur/Escape (design section
// 10.3). The parent decides push vs replace history (section 10.2 groups
// this with "text entry", so KitsCatalogue calls onChange with replace:true).
export default function SearchField({ q, onChange }) {
  const [draft, setDraft] = useState(q);
  const focusedRef = useRef(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    // Resync from the URL when it changes from outside (Back, a pasted
    // link) -- but never while the field has focus (section 10.3).
    if (!focusedRef.current) setDraft(q);
  }, [q]);

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  const commit = (value) => {
    clearTimeout(debounceRef.current);
    onChange(value.trim().slice(0, MAX_QUERY_LENGTH));
  };

  const handleChange = (e) => {
    const value = e.target.value;
    setDraft(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => commit(value), 250);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      commit(draft);
    } else if (e.key === "Escape") {
      setDraft("");
      commit("");
    }
  };

  const active = draft.trim().length > 0;

  return (
    <div className="relative h-9 w-full ml-auto sm:w-64">
      <Search
        size={14}
        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]"
      />
      <input
        type="search"
        value={draft}
        placeholder="Search by name"
        aria-label="Search by name"
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={() => (focusedRef.current = true)}
        onBlur={() => {
          focusedRef.current = false;
          commit(draft);
        }}
        className={`h-9 w-full rounded border bg-[#1a1a1e] pl-9 pr-8 text-sm text-white placeholder:text-[#A1A1AA] focus:border-[#FF5500] focus:outline-none transition-colors [&::-webkit-search-cancel-button]:hidden ${
          active ? "border-[#FF5500]" : "border-white/10"
        }`}
      />
      {active && (
        <button
          type="button"
          aria-label="Clear search"
          onClick={() => {
            setDraft("");
            commit("");
          }}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[#A1A1AA] hover:text-white"
        >
          <X size={14} />
        </button>
      )}
    </div>
  );
}
